import React, { useState, useEffect } from "react";
import { Container, InputLabel, Box, Modal, Paper, Grid, Tooltip, Typography, TextField, Button, FormControl, FormLabel, RadioGroup, Radio, FormControlLabel, Select, MenuItem, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Chip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import InfoIcon from "@mui/icons-material/Info";
import CloseIcon from "@mui/icons-material/Close";
import Snackbar from '@mui/material/Snackbar';
import MuiAlert from '@mui/material/Alert';

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

// ─────────────────────────────────────────────
// Info data for the dialog
// ─────────────────────────────────────────────
const editSubjectInfoSections = [
  {
    title: "Subject Name & Short Name",
    color: "#1976D2",
    description: "Update the full name of the subject and its abbreviated short name. Both fields are pre-filled with the existing values.",
    fields: [
      "Subject Name – Full name of the subject (required)",
      "Short Name – Abbreviated label for the subject (required)",
      "Pre-filled with existing data on page load",
    ],
  },
  {
    title: "Subject Group",
    color: "#7B1FA2",
    description: "Select the group/category this subject belongs to.",
    fields: [
      "Subject – Standard academic subject",
      "Practical – Lab or hands-on subject",
      "Oral – Spoken/verbal assessment subject",
    ],
  },
  {
    title: "Subject Type",
    color: "#388E3C",
    description: "Define whether this subject is mandatory, optional, or an additional offering for students.",
    fields: [
      "Compulsory – All students must take this subject",
      "Optional – Students may choose this subject",
      "Additional – Extra subject beyond the standard curriculum",
    ],
  },
  {
    title: "Time Table Priority",
    color: "#F57C00",
    description: "Set the scheduling priority for this subject when generating timetables.",
    fields: [
      "High – Scheduled first during timetable generation",
      "Normal – Standard scheduling priority",
      "Low – Scheduled last during timetable generation",
    ],
  },
  {
    title: "Image Upload",
    color: "#0277BD",
    description: "Optionally upload a new image or icon to replace the existing subject image.",
    fields: [
      "Select an image file using the file input",
      "Click 'Upload Image' to upload and get the new image path",
      "Uploaded image path is included in the update payload",
    ],
  },
  {
    title: "Actions",
    color: "#C62828",
    description: "Use the action buttons to submit the updated subject or cancel and go back.",
    fields: [
      "UPDATE – Submits changes via PUT request (disabled until all required fields are filled)",
      "CANCEL – Discards changes and navigates back to the previous page",
      "Required fields: Subject Name, Short Name, Subject Group, Subject Type, Priority",
    ],
  },
];

function UpdateSubMaster() {
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  // ── Info dialog state ──
  const [infoOpen, setInfoOpen] = useState(false);
  let navigate = useNavigate();
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [subjectData, setSubjectData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [imagePath, setImagePath] = useState('');
  const [formData, setFormData] = useState({
    subjectName: '',
    shortName: '',
    subjectGroupID: 0,
    subjectType: 0,
    gradeSystem: '',
    priority: '',
    imageURL: ''
  });

  const { subjectID } = useParams();

  const fetchSubjectData = async () => {
    try {
      setLoading(true);
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/SubjectMaster/GetSubject`;
      const token = sessionStorage.getItem('token');
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
      });

      if (response.ok) {
        const responseData = await response.json();
        setSubjectData(responseData);
        setLoading(false);
      } else {
        console.error('Failed to fetch subject data');
        setLoading(false);
      }
    } catch (error) {
      console.error('API request error:', error);
      setLoading(false);
      // alert('An error occurred while fetching subject data');
      setSnackbarMessage('An error occurred while fetching subject data');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  useEffect(() => {
    fetchSubjectData();
  }, []);

  useEffect(() => {
    const subject = subjectData.find(sub => sub.subjectID === parseInt(subjectID));
    if (subject) {
      setFormData({
        subjectName: subject.subjectName,
        shortName: subject.shortName,
        subjectGroupID: subject.subjectGroupID,
        subjectType: subject.subjectType,
        gradeSystem: subject.gradeSystem,
        priority: subject.priority,
        imageURL: subject.imageURL
      });
    } else {
      console.error('Subject not found');
    }
  }, [subjectID, subjectData]);

  const handleCancel = () => {
    navigate(-1);
  };

  const handleSave = async () => {
    try {
      const url = process.env.REACT_APP_BASE_URL;
      const apiUrl = `${url}/SubjectMaster/Id?Id=${subjectID}`;
      const token = sessionStorage.getItem('token');

      const payload = {
        subjectName: formData.subjectName,
        shortName: formData.shortName,
        subjectGroupID: formData.subjectGroupID,
        subjectType: formData.subjectType,
        gradeSystem: formData.gradeSystem || "",
        priority: formData.priority,
        imageURL: imagePath,
      };

      const response = await fetch(apiUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setFormData({
          subjectName: '',
          shortName: '',
          subjectGroupID: 0,
          subjectType: 0,
          gradeSystem: '',
          priority: '',
          imageURL: ''
        });
        setSelectedImage(null);
        setShowSuccessModal(true);
      } else {
        console.error('Failed to save submaster');
        // alert('Failed to update')
        setSnackbarMessage('Failed to update');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error('API request error:', error);
      // alert('An error occurred')
      setSnackbarMessage('An error occurred');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  const handleImageSelect = (e) => {
    setSelectedImage(e.target.files[0]);
  };

  const handleImageUpload = async () => {
    try {
      const formData = new FormData();
      formData.append('file', selectedImage);

      const token = sessionStorage.getItem('token');
      const apiUrl = process.env.REACT_APP_BASE_URL;
      const response = await fetch(`${apiUrl}/Employee/EmployeeImageUpload`, {
        method: 'POST',
        headers: {
          Authorization: token,
        },
        body: formData,
      });

      if (response.ok) {
        const responseData = await response.json();
        setImagePath(responseData.filePath);
        // alert('Image uploaded successfully.');
        setSnackbarMessage('Image uploaded successfully.');
        setOpenSnackbar(true);
        setSnackbarSeverity("success");
      } else {
        // alert('Failed to upload Image.');
        setSnackbarMessage('Failed to upload Image.');
        setOpenSnackbar(true);
        setSnackbarSeverity("error");
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      // alert('An error occurred while uploading image. Please try again later.');
      setSnackbarMessage('An error occurred while uploading image. Please try again later.');
      setOpenSnackbar(true);
      setSnackbarSeverity("error");
    }
  };

  return (
    <Container>
      <Box
        sx={{
          backgroundColor: '#0B1F40', // dark navy blue
          padding: '12px 24px',
          boxShadow: 3,
          borderRadius: '4px 4px 0 0',
          margin: '12px auto',
          width: "100%",
          display: 'flex',
          alignItems: 'center',
        }}
      >
        {/* ── Title + Info Button inline ── */}
        <Typography variant="h4" sx={{ color: '#fff', fontWeight: 'bold' }}>
          Edit Subject
        </Typography>
        <IconButton
          onClick={() => setInfoOpen(true)}
          title="Form Information"
          style={{
            backgroundColor: "rgba(255,255,255,0.15)",
            border: "2px solid rgba(255,255,255,0.6)",
            borderRadius: "50%",
            padding: "4px",
            color: "#fff",
            marginLeft: "10px",
          }}
        >
          <InfoIcon style={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* ── Info Dialog ── */}
      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ style: { borderRadius: 16, padding: 0 } }}
      >
        <DialogTitle
          style={{
            backgroundColor: "#0B1F40",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 24px",
          }}
        >
          <Box display="flex" alignItems="center">
            <InfoIcon style={{ marginRight: 8 }} />
            <Typography variant="h6" style={{ fontWeight: "bold" }}>
              Edit Subject — Form Guide
            </Typography>
          </Box>
          
        </DialogTitle>

        <DialogContent dividers style={{ backgroundColor: "#f9f9f9", padding: "24px" }}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 20 }}>
            This form allows you to update an existing subject record. All fields are pre-filled with the current values. Modify as needed and click UPDATE to apply changes.
          </Typography>

          {editSubjectInfoSections.map((section, index) => (
            <Box
              key={index}
              style={{
                backgroundColor: "#fff",
                borderRadius: 12,
                padding: "18px 20px",
                marginBottom: 16,
                borderLeft: `5px solid ${section.color}`,
                boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
              }}
            >
              <Typography
                variant="h6"
                style={{ fontWeight: "bold", color: section.color, marginBottom: 6 }}
              >
                {section.title}
              </Typography>

              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 10 }}>
                {section.description}
              </Typography>

              <Divider style={{ marginBottom: 10 }} />

              <Typography variant="caption" style={{ fontWeight: "bold", color: "#555", display: "block", marginBottom: 6 }}>
                📝 DETAILS
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {section.fields.map((field, i) => (
                  <Chip
                    key={i}
                    label={field}
                    size="small"
                    variant="outlined"
                    style={{
                      borderColor: section.color,
                      color: section.color,
                    }}
                  />
                ))}
              </Box>
            </Box>
          ))}
        </DialogContent>

        <DialogActions style={{ padding: "12px 24px", backgroundColor: "#f9f9f9" }}>
          <Button
            onClick={() => setInfoOpen(false)}
            variant="contained"
            style={{ backgroundColor: "#0B1F40", color: "#fff", borderRadius: 20, paddingInline: 28 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Paper elevation={3} style={{ padding: 20 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField label="Subject Name" fullWidth value={formData.subjectName} onChange={(e) => setFormData({ ...formData, subjectName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField label="Short Name" fullWidth value={formData.shortName} onChange={(e) => setFormData({ ...formData, shortName: e.target.value })} />
          </Grid>
          <Grid item xs={12} md={6}>
            {/* <FormControl fullWidth>
              <FormLabel>Subject Group</FormLabel>
              <Select value={formData.subjectGroupID} onChange={(e) => setFormData({ ...formData, subjectGroupID: e.target.value })} fullWidth>
                <MenuItem value="">--Select--</MenuItem>
                <MenuItem value="1">Subject</MenuItem>
                <MenuItem value="2">Practical</MenuItem>
                <MenuItem value="3">Oral</MenuItem>
              </Select>
            </FormControl> */}
          </Grid>
          <Grid item xs={12} md={6}>
            {/* <FormControl fullWidth>
              <FormLabel>Subject Type</FormLabel>
              <RadioGroup row value={formData.subjectType} onChange={(e) => setFormData({ ...formData, subjectType: e.target.value })}>
                <FormControlLabel value="1" control={<Radio />} label="Compulsory" />
                <FormControlLabel value="2" control={<Radio />} label="Optional" />
                <FormControlLabel value="3" control={<Radio />} label="Additional" />
              </RadioGroup>
            </FormControl> */}
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel>Subject's Grading System</FormLabel>
              <RadioGroup row value={formData.gradeSystem} onChange={(e) => setFormData({ ...formData, gradeSystem: e.target.value })}>
                <FormControlLabel value="Nine" control={<Radio />} label="Nine" />
                <FormControlLabel value="Six" control={<Radio />} label="Six" />
                <FormControlLabel value="Three" control={<Radio />} label="Three" />
              </RadioGroup>
            </FormControl>
          </Grid> */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel>Time Table Priority</FormLabel>
              <RadioGroup row value={formData.priority} onChange={(e) => setFormData({ ...formData, priority: e.target.value })}>
                <FormControlLabel value="High" control={<Radio />} label="High" />
                <FormControlLabel value="Normal" control={<Radio />} label="Normal" />
                <FormControlLabel value="Low" control={<Radio />} label="Low" />
              </RadioGroup>
            </FormControl>
          </Grid>
          {/* <Grid item xs={12} md={6}>
            <InputLabel htmlFor="resume">Image</InputLabel>
            <input
              type="file"
              className="form-control"
              id="resume"
              onChange={handleImageSelect}
            />
            <Button variant="contained" component="span" onClick={handleImageUpload}>
              Upload Image
            </Button>
          </Grid> */}
          <Grid item xs={12}>
            <Box display="flex" >
              <Tooltip title={(!formData.subjectName || !formData.shortName || !formData.subjectGroupID || !formData.priority || !formData.subjectType) && "Please fill all fields"} arrow><span>
                <Button variant="contained" color="primary" onClick={handleSave} style={{
                  width: "100px",
                  height: "50px",
                  padding: "10px 0",
                  fontWeight: 'bold'
                }}
                  disabled={!formData.subjectName || !formData.shortName  || !formData.priority }>
                  UPDATE
                </Button></span></Tooltip>
              <Button variant="contained" color="error" onClick={handleCancel} style={{
                marginLeft: 10, width: "100px",
                height: "50px",
                padding: "10px 0",
                fontWeight: 'bold'
              }}>
                CANCEL
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Modal
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        aria-labelledby="success-modal-title"
        aria-describedby="success-modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 400,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 24,
            overflow: 'hidden',
          }}
        >
          {/* Header */}
          <Box sx={{ backgroundColor: '#0B1F40', padding: '12px 16px' }}>
            <Typography id="success-modal-title" variant="h6" sx={{ color: '#fff' }}>
              ✅ Subject Updated
            </Typography>
          </Box>

          {/* Content */}
          <Box sx={{ padding: '20px' }}>
            <Typography id="success-modal-description" sx={{ mb: 2 }}>
              The subject has been Updated successfully.
            </Typography>

            {/* Buttons */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <Button
                onClick={() => {
                  setShowSuccessModal(false);
                  navigate(-1); // go back after closing modal
                }}
                variant="contained"
                color="primary"
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Modal>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default UpdateSubMaster;